import { useCallback, useEffect, useState, type FormEvent, type ReactNode } from "react";
import { Link } from "react-router-dom";
import { Alert } from "../../components/Alert";
import { BillingStatusBadge } from "../../components/billing/BillingBadges";
import { Pagination } from "../../components/crm/Pagination";
import { RequirePermission } from "../../components/crm/RequirePermission";
import { Spinner } from "../../components/Spinner";
import { isApiError, listInvoices } from "../../lib/billing-api";
import type { Invoice, InvoiceListParams, InvoiceStatus } from "../../lib/billing-types";
import { formatDate, formatMoney } from "../../lib/format";
import { useConfig } from "../../state/config-context";
import { useSession } from "../../state/session-context";

const PAGE_LIMIT = 25;

interface Filters {
  q: string;
  status: "" | InvoiceStatus;
}

const EMPTY_FILTERS: Filters = { q: "", status: "" };

function InvoicesInner(): ReactNode {
  const { t, lang } = useConfig();
  const { hasPermission } = useSession();
  const canCreate = hasPermission("billing.invoice.create");

  const [filters, setFilters] = useState<Filters>(EMPTY_FILTERS);
  const [search, setSearch] = useState("");
  const [items, setItems] = useState<Invoice[]>([]);
  const [nextCursor, setNextCursor] = useState<string | null>(null);
  const [cursorStack, setCursorStack] = useState<(string | undefined)[]>([]);
  const [cursor, setCursor] = useState<string | undefined>(undefined);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const locale = lang === "es" ? "es-ES" : "en-US";

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const params: InvoiceListParams = { limit: PAGE_LIMIT, cursor };
      if (filters.q !== "") params.q = filters.q;
      if (filters.status !== "") params.status = filters.status;
      const page = await listInvoices(params);
      setItems(page.items);
      setNextCursor(page.next_cursor);
    } catch (caught) {
      setError(isApiError(caught) ? caught.detail : t("error.network"));
    } finally {
      setLoading(false);
    }
  }, [cursor, filters, t]);

  useEffect(() => {
    void load();
  }, [load]);

  const resetCursor = (): void => {
    setCursor(undefined);
    setCursorStack([]);
  };

  const onFilter = (event: FormEvent<HTMLFormElement>): void => {
    event.preventDefault();
    resetCursor();
    setFilters({ q: search.trim(), status: filters.status });
  };

  const onClear = (): void => {
    setSearch("");
    resetCursor();
    setFilters(EMPTY_FILTERS);
  };

  const hasPrev = cursorStack.length > 0;

  return (
    <section className="page" aria-labelledby="billing-invoices-title">
      <header className="page__header page__header--row">
        <div>
          <h1 id="billing-invoices-title">{t("billing.invoices.title")}</h1>
          <p className="muted">{t("billing.invoices.subtitle")}</p>
        </div>
        {canCreate ? (
          <Link className="btn btn--primary" to="/billing/invoices/new">
            {t("billing.invoices.new")}
          </Link>
        ) : null}
      </header>

      {error !== null ? <Alert tone="error">{error}</Alert> : null}

      <form className="filters" onSubmit={onFilter}>
        <label className="field" htmlFor="billing-invoices-q">
          <span>{t("billing.common.search")}</span>
          <input
            id="billing-invoices-q"
            value={search}
            placeholder={t("billing.invoices.searchPlaceholder")}
            onChange={(event) => setSearch(event.target.value)}
          />
        </label>
        <label className="field" htmlFor="billing-invoices-status">
          <span>{t("billing.filter.status")}</span>
          <select
            id="billing-invoices-status"
            value={filters.status}
            onChange={(event) => {
              resetCursor();
              setFilters((current) => ({
                ...current,
                status: event.target.value as Filters["status"],
              }));
            }}
          >
            <option value="">{t("billing.common.all")}</option>
            <option value="draft">{t("billing.status.draft")}</option>
            <option value="sent">{t("billing.status.sent")}</option>
            <option value="paid">{t("billing.status.paid")}</option>
            <option value="overdue">{t("billing.status.overdue")}</option>
            <option value="void">{t("billing.status.void")}</option>
          </select>
        </label>
        <div className="filters__actions">
          <button type="submit" className="btn btn--primary">
            {t("billing.common.apply")}
          </button>
          <button type="button" className="btn btn--ghost" onClick={onClear}>
            {t("billing.common.clear")}
          </button>
        </div>
      </form>

      {loading ? <Spinner label={t("app.loading")} /> : null}

      {!loading && items.length === 0 ? (
        <p className="empty">{t("billing.invoices.empty")}</p>
      ) : null}

      {!loading && items.length > 0 ? (
        <div className="table-wrap">
          <table className="table">
            <caption className="sr-only">{t("billing.invoices.title")}</caption>
            <thead>
              <tr>
                <th scope="col">{t("billing.invoice.number")}</th>
                <th scope="col">{t("billing.invoice.account")}</th>
                <th scope="col">{t("billing.invoice.status")}</th>
                <th scope="col">{t("billing.invoice.dueDate")}</th>
                <th scope="col">{t("billing.invoice.total")}</th>
                <th scope="col">{t("billing.invoice.balanceDue")}</th>
                <th scope="col" className="table__actions-col">
                  <span className="sr-only">{t("billing.common.actions")}</span>
                </th>
              </tr>
            </thead>
            <tbody>
              {items.map((invoice) => (
                <tr key={invoice.id}>
                  <td>
                    <Link to={`/billing/invoices/${invoice.id}`}>
                      {invoice.number === null
                        ? t("billing.quote.draftNumber")
                        : `#${String(invoice.number)}`}
                    </Link>
                  </td>
                  <td>{invoice.account_id}</td>
                  <td>
                    <BillingStatusBadge status={invoice.status} />
                  </td>
                  <td>{formatDate(invoice.due_date, locale)}</td>
                  <td>{formatMoney(invoice.total_minor, invoice.currency, locale)}</td>
                  <td>{formatMoney(invoice.balance_due_minor, invoice.currency, locale)}</td>
                  <td className="table__actions-col">
                    <Link className="btn btn--ghost btn--sm" to={`/billing/invoices/${invoice.id}`}>
                      {t("billing.common.view")}
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : null}

      <Pagination
        hasPrev={hasPrev}
        hasNext={nextCursor !== null}
        busy={loading}
        onPrev={() => {
          if (cursorStack.length === 0) {
            return;
          }
          const previous = cursorStack[cursorStack.length - 1];
          setCursorStack((stack) => stack.slice(0, -1));
          setCursor(previous);
        }}
        onNext={() => {
          if (nextCursor === null) {
            return;
          }
          setCursorStack((stack) => [...stack, cursor]);
          setCursor(nextCursor);
        }}
      />
    </section>
  );
}

export function InvoicesPage(): ReactNode {
  return (
    <RequirePermission permission="billing.invoice.read">
      <InvoicesInner />
    </RequirePermission>
  );
}
